import { Fragment, useMemo, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { useMe } from '../me/use-me.js';
import { useAuditEvents, type AuditEvent, type AuditFilters } from './use-audit.js';

function outcomeStyle(outcome: AuditEvent['outcome']): string {
  if (outcome === 'success') return 'bg-green-100 text-green-800';
  if (outcome === 'denied') return 'bg-amber-100 text-amber-800';
  return 'bg-red-100 text-red-800';
}

const DETAIL_COLSPAN = 6;

function DetailField(props: { label: string; value: string | null }): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{props.label}</dt>
      <dd className="whitespace-pre-wrap break-words font-mono text-xs text-slate-800">
        {props.value === null || props.value === '' ? (
          <span className="text-slate-400">—</span>
        ) : (
          props.value
        )}
      </dd>
    </div>
  );
}

function AuditDetailPanel(props: { event: AuditEvent }): JSX.Element {
  const e = props.event;
  const occurredAtDate = new Date(e.occurredAt);
  const occurredAtHuman = occurredAtDate.toLocaleString();
  const userAgent = e.userAgent ?? null;
  return (
    <tr>
      <td colSpan={DETAIL_COLSPAN} className="bg-slate-50 px-4 py-4">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
          <DetailField label="action" value={e.action} />
          <DetailField label="occurredAt" value={`${occurredAtHuman} (${e.occurredAt})`} />
          <DetailField label="outcome" value={e.outcome} />
          <DetailField label="reason" value={e.reason} />
          <DetailField label="companyId" value={e.companyId} />
          <DetailField label="actorType" value={e.actorType} />
          <DetailField label="actorId" value={e.actorId} />
          <DetailField label="targetType" value={e.targetType} />
          <DetailField label="targetId" value={e.targetId} />
          <DetailField label="requestId" value={e.requestId} />
          <DetailField label="ip" value={e.ip} />
          <DetailField label="userAgent" value={userAgent} />
        </dl>
      </td>
    </tr>
  );
}

export function AuditPage(): JSX.Element {
  const { me } = useMe();
  const hasIonic = (me?.ionicRoles ?? []).length > 0;
  const companyOptions = me?.companies ?? [];

  const [companyId, setCompanyId] = useState<string>('');
  const [action, setAction] = useState<string>('');
  const [outcome, setOutcome] = useState<'' | 'success' | 'failure' | 'denied'>('');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [actorId, setActorId] = useState<string>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleExpanded = (id: string): void => {
    setExpandedId((curr) => (curr === id ? null : id));
  };

  const handleRowKeyDown = (e: KeyboardEvent<HTMLTableRowElement>, id: string): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleExpanded(id);
    }
  };

  const filters: AuditFilters = useMemo(() => {
    const f: AuditFilters = {};
    if (companyId) f.companyId = companyId;
    if (action) f.action = action;
    if (outcome) f.outcome = outcome;
    if (actorId) f.actorId = actorId;
    if (from) f.from = new Date(from).toISOString();
    if (to) f.to = new Date(to).toISOString();
    return f;
  }, [companyId, action, outcome, actorId, from, to]);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useAuditEvents(filters);

  const rows = (data?.pages ?? []).flatMap((p) => p.items);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Audit log</h1>
        <Link to="/" className="text-sm text-brand-600 hover:text-brand-700">
          ← Back
        </Link>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {hasIonic ? (
            <div className="col-span-1">
              <label className="mb-1 block text-xs font-medium text-slate-600">Company</label>
              <select
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
              >
                <option value="">All companies</option>
                {companyOptions.map((c) => (
                  <option key={c.companyId} value={c.companyId}>
                    {c.companyName}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="col-span-1">
            <label className="mb-1 block text-xs font-medium text-slate-600">Action</label>
            <input
              type="text"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder="e.g. run.created"
              className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm font-mono"
            />
          </div>

          <div className="col-span-1">
            <label className="mb-1 block text-xs font-medium text-slate-600">Outcome</label>
            <select
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as '' | 'success' | 'failure' | 'denied')}
              className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
            >
              <option value="">Any</option>
              <option value="success">success</option>
              <option value="failure">failure</option>
              <option value="denied">denied</option>
            </select>
          </div>

          <div className="col-span-1">
            <label className="mb-1 block text-xs font-medium text-slate-600">Actor ID</label>
            <input
              type="text"
              value={actorId}
              onChange={(e) => setActorId(e.target.value)}
              placeholder="user uuid or worker:..."
              className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm font-mono"
            />
          </div>

          <div className="col-span-1">
            <label className="mb-1 block text-xs font-medium text-slate-600">From</label>
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
            />
          </div>

          <div className="col-span-1">
            <label className="mb-1 block text-xs font-medium text-slate-600">To</label>
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
            />
          </div>
        </div>
      </section>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {error.message}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2 font-medium">When</th>
              <th className="px-3 py-2 font-medium">Action</th>
              <th className="px-3 py-2 font-medium">Outcome</th>
              <th className="px-3 py-2 font-medium">Actor</th>
              <th className="px-3 py-2 font-medium">Target</th>
              <th className="px-3 py-2 font-medium">Company</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={DETAIL_COLSPAN} className="px-3 py-6 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={DETAIL_COLSPAN} className="px-3 py-6 text-center text-slate-500">
                  No audit events match the current filters.
                </td>
              </tr>
            ) : (
              rows.map((e) => {
                const isExpanded = expandedId === e.id;
                return (
                  <Fragment key={e.id}>
                    <tr
                      role="button"
                      tabIndex={0}
                      aria-expanded={isExpanded}
                      data-testid={`audit-row-${e.id}`}
                      onClick={() => toggleExpanded(e.id)}
                      onKeyDown={(ev) => handleRowKeyDown(ev, e.id)}
                      className={clsx(
                        'cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-500',
                        isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50',
                      )}
                    >
                      <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-slate-700">
                        {new Date(e.occurredAt).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-800">{e.action}</td>
                      <td className="px-3 py-2">
                        <span
                          className={clsx(
                            'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                            outcomeStyle(e.outcome),
                          )}
                        >
                          {e.outcome}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">
                        {e.actorType}: <span className="font-mono">{e.actorId ?? '—'}</span>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">
                        {e.targetType ? (
                          <>
                            {e.targetType}
                            {e.targetId ? (
                              <>
                                : <span className="font-mono">{e.targetId.slice(0, 8)}…</span>
                              </>
                            ) : null}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">
                        {e.companyId ? e.companyId.slice(0, 8) + '…' : 'ionic'}
                      </td>
                    </tr>
                    {isExpanded && <AuditDetailPanel event={e} />}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          {rows.length} event{rows.length === 1 ? '' : 's'} loaded
        </p>
        {hasNextPage && (
          <button
            type="button"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            className="rounded-md border border-slate-300 px-3 py-1 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>
    </div>
  );
}
